import { useEffect, useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { useCloneProd } from '@/hooks/use-clone-prod';
import type { CloneProdJob } from '@/types';

const NEW_BRAND = '__new__';

const STEP_LABEL: Record<NonNullable<CloneProdJob['step']>, string> = {
  export: 'Reading the production account',
  documents: 'Copying protocols, SOPs, sources, personas and settings',
  s3: 'Copying documents and uploads',
  vectors: 'Copying the search index'
};

const summarize = (job: CloneProdJob) => {
  const docs = Object.values(job.result.documents).reduce((a, b) => a + b, 0);
  const vectors = Object.values(job.result.vectors).reduce((a, b) => a + b, 0);
  return `${docs} records, ${job.result.s3Objects} files and ${vectors} search entries copied`;
};

interface Props {
  trigger: React.ReactNode;
}

/**
 * Copy a production account's training data into one brand of this (dev) account.
 *
 * Only the production account id and the target brand are asked for: everything else is
 * decided by kim-ai-backend, which also refuses the request outside a dev stack.
 */
export function CloneProdDialog({ trigger }: Props) {
  const { data: units = [] } = useBusinessUnits();
  const { job, start, reset } = useCloneProd();
  const [open, setOpen] = useState(false);
  const [fromAccountId, setFromAccountId] = useState('');
  const [target, setTarget] = useState(NEW_BRAND);
  const [brandName, setBrandName] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (open) {
      setFromAccountId('');
      setTarget(NEW_BRAND);
      setBrandName('');
      reset();
    }
    // reset is stable for the dialog's lifetime; re-running on it would clear a live job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (job?.status === 'done') toast.success(`Clone finished: ${summarize(job)}`);
    if (job?.status === 'failed') toast.error(job.error ?? 'Clone failed');
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const from = Number(fromAccountId);
  const valid =
    Number.isInteger(from) && from > 0 && (target !== NEW_BRAND || brandName.trim().length > 0);

  const onStart = async () => {
    setStarting(true);
    try {
      await start({
        fromAccountId: from,
        ...(target === NEW_BRAND ? { brandName: brandName.trim() } : { buId: Number(target) })
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start the clone');
    } finally {
      setStarting(false);
    }
  };

  const running = job?.status === 'running';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clone from production</DialogTitle>
          <DialogDescription>
            Copies a production account&apos;s protocols, SOPs, sources, personas, promoted
            workflows and settings into one brand of this account. Nothing in production is
            changed. Running it again for the same production account replaces the earlier
            copy.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='clone-from'>Production account ID</Label>
            <Input
              id='clone-from'
              inputMode='numeric'
              autoFocus
              disabled={running}
              value={fromAccountId}
              onChange={(e) => setFromAccountId(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='clone-target'>Into brand</Label>
            <Select value={target} onValueChange={setTarget} disabled={running}>
              <SelectTrigger id='clone-target'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW_BRAND}>New brand…</SelectItem>
                {units.map((bu) => (
                  <SelectItem key={bu.id} value={String(bu.id)}>
                    {bu.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {target === NEW_BRAND && (
            <div className='space-y-1.5'>
              <Label htmlFor='clone-brand'>New brand name</Label>
              <Input
                id='clone-brand'
                disabled={running}
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </div>
          )}
          {job && (
            <p className='text-sm text-muted-foreground' aria-live='polite'>
              {job.status === 'running' && `${job.step ? STEP_LABEL[job.step] : 'Starting'}…`}
              {job.status === 'done' && `Done. ${summarize(job)}.`}
              {job.status === 'failed' && `Failed: ${job.error}`}
            </p>
          )}
        </div>
        <DialogFooter>
          {job && job.status !== 'running' ? (
            <Button type='button' onClick={() => setOpen(false)}>
              Close
            </Button>
          ) : (
            <Button type='button' onClick={onStart} disabled={!valid || starting || running}>
              {running ? 'Cloning…' : starting ? 'Starting…' : 'Start clone'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
